/* eslint-disable */
import React, { FC, useState } from 'react';
import { useMutation } from 'react-query';
import { Upload, Modal, Spin, Typography, Button } from 'antd';
import { UploadChangeParam } from 'antd/lib/upload';
import { RcFile, UploadFile, UploadProps } from 'antd/lib/upload/interface';
import styled, { css } from 'styled-components';
import { useMediaQuery } from 'react-responsive';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { useDirection } from '~/hooks/i18nHooks';

import { uploadImage } from '~/api/file';
import useFileList, { rcFileToAndtImage } from '~/hooks/useFileList';
import useImageCache from '~/hooks/useImageCache';
import { ImageResponse } from '~/types/files';
import colors from '~/constants/colors';
import features from '~/constants/features';
import humanFileSize from '~/utils/humanFileSize';
import theme from '~/constants/theme';
import { useInputContext } from '~/components/Form/InputContext';
import DocumentInputContent from './DocumentInputContent';
import DocumentInputContentMobile from './DocumentInputContentMobile';
import { DeleteOutlined, FileOutlined, FilePdfFilled, FilePdfOutlined } from '@ant-design/icons';
import { PdfIcon } from '~/img';

interface Props extends Omit<UploadProps, 'onChange'> {
  label?: string;
  name: string;
  onChange?: (values: ImageResponse | ImageResponse[] | undefined) => void;
  onUploadError?: (error: any) => void;
  value?: ImageResponse | ImageResponse[];
  maxCount?: number;
  maxSizeB?: number;
  multiple?: boolean;
  noRemove?: boolean;
  variant?: 'default' | 'drawer';
  [x: string]: any;
  readonly?: boolean;
  styleType?: any;
  infoIcon?: React.ReactNode;
}

const DocumentInput: FC<Props> = (props: any) => {
  const {
    name,
    label,
    onChange,
    onUploadError,
    value = [],
    maxCount = features.documentFileInput.maxCount,
    maxSizeB = features.documentFileInput.maxSizeB,
    multiple = true,
    variant = 'default',
    id,
    disabled,
    readonly,
    noRemove = false,
    className,
    labelText,
    styleType,
    infoIcon,

    ...rest
  } = props;

  const [previewState, setPreviewState] = useState(previewInitialState);
  const [errorMessage, setErrorMessage] = useState('');

  const fileList = useFileList([value].flat(1));
  const imagePost = useMutation(uploadImage);
  const [, setImageCache] = useImageCache();
  const inputContext = useInputContext();

  const { t } = useTranslation('ksaApplication');
  const dir = useDirection();

  const handlers = {
    // Upload
    beforeUpload: async (rcFile: RcFile) => {
      console.log('rcFile', rcFile);
      setErrorMessage('');
      if (rcFile.size > maxSizeB) {
        console.log('rcFile.size', rcFile);
        setErrorMessage(
          `Your file is ${humanFileSize(rcFile.size)}, but ${humanFileSize(maxSizeB)} is allowed`
        );
        return false;
      }
      try {
        const res = await imagePost.mutateAsync(rcFile);
        console.log('resres', res);
        setImageCache((prev) => ({
          ...prev,
          [res.id]: {
            isLoading: false,
            error: null,
            antdImage: rcFileToAndtImage(rcFile, res.id, res.mimeType),
            apiImage: res,
          },
        }));
        onChange?.(multiple ? (value as ImageResponse[]).concat([res]) : res);
      } catch (error: any) {
        const errorData = error?.response?.data;
        onUploadError?.(errorData ?? error);
      }
      return false;
    },
    onRemove: (file: UploadFile<any>) => {
      onChange?.(
        multiple ? (value as ImageResponse[]).filter((item) => item.id !== file.uid) : undefined
      );
      return false;
    },
    // Preview
    onPreview: (file: UploadFile<any>) => {
      const imageUrl = file.thumbUrl;
      if (imageUrl) {
        setPreviewState({
          open: true,
          name: file.name,
          imageUrl,
          type: file.type ?? '',
        });
      }
      return false;
    },
    onClose: () => {
      setPreviewState(previewInitialState);
    },
  };

  function itemRender(_: any, file: any) {
    console.log('filefile', file);
    const isPdf = file.type === 'application/pdf';
    return (
      <div>
        <ItemContainer onClick={() => handlers?.onPreview(file)}>
          <div className="afterUpload-parent">
            {isPdf ? (
              <FilePdfOutlined className="afterUpload-Icon" />
            ) : (
              <img className="afterUpload-image" src={file.thumbUrl} />
            )}
            <div className="afterUpload-text">{file.name}</div>
          </div>
          <div>
            <DeleteOutlined
              onClick={(e) => {
                e.stopPropagation();
                handlers?.onRemove(file);
              }}
              className="afterUpload-delete-icon"
            />
          </div>
        </ItemContainer>
        {/* <div */}
        {/*   style={{ */}
        {/*     color: '#313136', */}
        {/*     textAlign: 'center', */}
        {/*     fontFamily: 'Inter, sans-serif', */}
        {/*     fontSize: '12px', */}
        {/*     fontStyle: 'normal', */}
        {/*     fontWeight: 300, */}
        {/*     // lineHeight: '24px', */}
        {/*     letterSpacing: '0.14px', */}
        {/*     marginTop: '8px', */}
        {/*   }} */}
        {/* > */}
        {/*   {t('Supported files:')} */}
        {/*   <br /> */}
        {/*   {t('JPG, JPEG, PNG, PDF. Maximum size: 10 MB.')} */}
        {/* </div> */}
      </div>
    );
  }

  const isTablet = useMediaQuery({ maxWidth: theme.breakpoints.md });

  const isLimitReached = typeof maxCount === 'number' && maxCount === fileList?.length;
  const isNonMultipleLimitReached = multiple === false && fileList?.length === 1;

  return (
    <>
      <Container
        className="DocumentInput"
        variant={variant}
        disabled={!!disabled}
        multiple={multiple}
        noRemove={noRemove}
        dir={dir}
      >
        <Upload
          style={{ width: '100%' }}
          id={id}
          accept={features.documentFileInput.allowedFileTypes.join(', ')}
          name={name}
          listType={'picture-card'}
          itemRender={itemRender}
          disabled={imagePost.isLoading || disabled || readonly}
          fileList={fileList as any}
          beforeUpload={handlers.beforeUpload}
          onRemove={handlers.onRemove}
          onPreview={handlers.onPreview}
          maxCount={maxCount}
          multiple={multiple}
          iconRender={(file) => (file.status === 'uploading' ? <Spin /> : <FileOutlined />)}
          isImageUrl={(file): boolean => {
            return !!file.type?.startsWith('image/');
          }}
          className={cx('DocumentInput-Upload', className)}
          {...rest}
        >
          {isLimitReached || isNonMultipleLimitReached ? null : isTablet ? (
            <DocumentInputContent
              styleType={styleType}
              labelText={labelText}
              maxSizeB={maxSizeB}
              loading={imagePost.isLoading}
              optional={inputContext.optional}
              infoIcon={infoIcon}
            />
          ) : (
            <DocumentInputContent
              styleType={styleType}
              labelText={labelText}
              maxSizeB={maxSizeB}
              loading={imagePost.isLoading}
              optional={inputContext.optional}
              infoIcon={infoIcon}
            />
          )}
        </Upload>

        <Modal
          open={previewState.open}
          title={previewState.name}
          footer={
            <Button
              style={{
                background: colors.blue[800],
                padding: '0 24px',
                height: 48,
                borderRadius: 8,
                color: 'white',
                border: 'none',
              }}
              onClick={() => {
                const link = document.createElement('a');
                link.href = previewState.imageUrl;
                link.download = previewState.name;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
            >
              {t('Download File')}
            </Button>
          }
          onCancel={handlers.onClose}
          centered
        >
          {
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
              {previewState.type === 'application/pdf' ? (
                <div style={{ textAlign: 'center' }}>
                  <FilePdfOutlined style={{ fontSize: 120, color: '#6598ba' }} />
                  <Typography.Paragraph style={{ marginTop: 16 }}>
                    {previewState.name}
                  </Typography.Paragraph>
                </div>
              ) : (
                <img alt={previewState.name} style={fullWidth} src={previewState.imageUrl} />
              )}
            </div>
          }
        </Modal>
        {errorMessage && (
          <div className="DocumentInput-ErrorMessage" role="alert">
            {errorMessage}
          </div>
        )}
        {!fileList?.length && (
          <div className="DocumentInputContent-BottomText">
            {dir === 'rtl' ? (
              t('Files supported: JPG, JPEG, PNG, PDF. Max size {{size}}', {
                size: t(humanFileSize(maxSizeB, true, 0)),
              })
            ) : (
              <>
                {t('Supported files: JPG, JPEG, PNG, PDF.')}
                <br />
                {t('Maximum size: {{size}}.', {
                  size: humanFileSize(maxSizeB, true, 0).replace(/(\d+)([A-Za-z]+)/, '$1 $2'),
                })}
              </>
            )}
          </div>
        )}
      </Container>
    </>
  );
};

const previewInitialState = {
  open: false,
  name: '',
  imageUrl: '',
  type: '',
};

const fullWidth = { width: '100%' };

interface UIProps {
  variant: Props['variant'];
  multiple: boolean;
  disabled: boolean;
  noRemove: boolean;
}

const ItemContainer = styled.div`
  width: 100%;
  display: flex;
  cursor: pointer;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  background-color: white;
  //gap: 12px;
  border: 1px solid #c2d9ec;
  border-radius: 8px;
  padding: 12px;

  .afterUpload-Icon {
    font-size: 50px;
    color: #6598ba;
  }

  .afterUpload-parent {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 12px;
  }

  .afterUpload-image {
    border-radius: 4px;
    height: 60px;
    width: 50px;
  }

  .afterUpload-text {
    align-items: center;
    width: 200px;
  }

  .afterUpload-delete-icon {
    z-index: 999;
    color: #0069b1;
    text-align: right;
  }
`;

const Container = styled.div<UIProps & { dir?: string }>`
  width: 100%;
  direction: ${({ dir }) => dir || 'ltr'};
  text-align: ${({ dir }) => (dir === 'rtl' ? 'right' : 'left')};

  .DocumentInputContent-BottomText {
    color: #4a4a4a;
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
    font-size: 12px;
    font-style: normal;
    font-weight: 300;
    line-height: 18px; /* 150% */
    letter-spacing: 0.24px;
    text-align: ${({ dir }) => (dir === 'rtl' ? 'right' : 'left')};
  }
  display: block;
  .DocumentInput-Upload {
    width: 100%;
    ${({ noRemove }) =>
    noRemove
      ? css`
            button {
              display: none;
            }
          `
      : css``}
  }
  .ant-upload-list {
    display: flex;

    ${({ multiple }) =>
    multiple
      ? css`
            @media (min-width: ${theme.breakpoints.md}px) {
              flex-direction: row-reverse;
              flex-wrap: wrap-reverse;
            }
          `
      : css`
            flex-direction: row;
            flex-wrap: wrap;
          `}
  }
  .ant-upload-select {
    width: 100%;
  }
  .ant-upload-list-text {
    flex-direction: column;
    max-width: ${theme.formInputMaxWidth}px;
    @media (max-width: ${theme.breakpoints.xs}px) {
      max-width: calc(100vw - 46px);
      .ant-upload-list-text-container {
        width: 100%;
      }
    }
    .ant-upload-list-item-info {
      border-radius: ${theme.borderRadiusSm}px;
    }
    .ant-upload-list-item-card-actions-btn {
      opacity: 1;
    }
    .ant-upload-list-item-list-type-text {
      margin-right: -8px;
      &:hover {
        .ant-upload-list-item-info {
          background-color: unset;
        }
      }
    }
  }
  .ant-upload.ant-upload-select-picture-card {
    border: 1px solid ${colors.grey[300]};
    background: #fbfbfb;
    flex: 1;
    width: 100%;

    min-width: 220px;
    &:hover {
      border-color: ${colors.grey[200]} !important;
    }
  }
  .ant-upload-list-item {
    height: 104px;
    width: 100%;
  }
  .ant-upload-picture-card-wrapper {
    position: relative;
    width: 100%;
  }
  .ant-upload-disabled {
    opacity: 0.5;
  }
  .ant-upload-list-picture-card-container {
    width: 100%;
    opacity: ${({ disabled }) => (disabled ? 0.5 : undefined)};
  }
  .DocumentInput-ErrorMessage {
    color: ${colors.errorColor};
    font-size: 12px;
    position: absolute;
    bottom: -8px;
  }
  ${({ variant }) =>
    variant === 'drawer' &&
    css`
      .ant-upload-list-picture-card-container {
        width: 100%;
        height: unset;
        aspect-ratio: 1;
        &:nth-child(2n) {
          margin-right: 0px;
        }
      }
      .ant-upload.ant-upload-select-picture-card {
        width: calc(50% - 4px);
        height: unset;
        aspect-ratio: 1;
        margin-right: 0px;
      }
    `}
`;

export type DocumentInputOnChangeEvent = UploadChangeParam<UploadFile<any>> & {
  value: ImageResponse;
};

export default DocumentInput;
